/**
 * What an email address can and cannot say about a broker, as the server reads it.
 *
 * The Intake card (`BrokerResolutionCard`) makes two judgements of its own before
 * anything reaches the server, and both must agree with the server's:
 *
 *   * Which brokerage LISTS the sender's domain. The card preselected a firm row
 *     holding "onmicrosoft.com" for every Microsoft 365 default-tenant sender
 *     (review finding 9, re-verification): the server refuses such a domain on
 *     every path (`app/core/broker_domains.py`, `is_corporate_domain`), but a row
 *     that learned it before that guard is still in the directory, and the card
 *     had no notion of a shared domain. The two lists below are the server's,
 *     word for word; `backend/tests/test_broker_confirmation_evidence.py` reads
 *     this file and fails if either drifts.
 *   * Whether confirming an existing contact will also keep the record's address
 *     on them. The server keeps it only when the mailbox is built from the
 *     person's name (`app/services/contact_names.py`, `address_names_person`): a
 *     team mailbox is not one broker's. `addressNamesPerson` is the same rule,
 *     held to the same table (`backend/tests/fixtures/broker_personal_address.json`).
 */

/** Mail providers and consumer ISPs: a broker on gmail.com belongs to their firm, not to "Gmail". */
export const FREEMAIL_DOMAINS: ReadonlySet<string> = new Set<string>([
  // Webmail
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'msn.com', 'yahoo.com', 'ymail.com', 'rocketmail.com', 'aol.com',
  'icloud.com', 'me.com', 'mac.com', 'proton.me', 'protonmail.com', 'pm.me',
  'gmx.com', 'gmx.net', 'mail.com', 'zoho.com', 'yandex.com', 'yandex.ru',
  'mail.ru', 'fastmail.com', 'fastmail.fm', 'hey.com', 'duck.com', 'tutanota.com',
  'tuta.io', 'hushmail.com', 'inbox.com',
  // Country variants a US desk still sees
  'yahoo.co.uk', 'outlook.co.uk', 'hotmail.co.uk', 'yahoo.ca', 'hotmail.ca',
  'live.co.uk', 'btinternet.com',
  // US consumer ISPs — small CRE shops run on these
  'comcast.net', 'xfinity.com', 'verizon.net', 'sbcglobal.net', 'att.net',
  'bellsouth.net', 'cox.net', 'optonline.net', 'optimum.net', 'earthlink.net',
  'charter.net', 'spectrum.net', 'rr.com', 'roadrunner.com', 'twc.com',
  'frontier.com', 'frontiernet.net', 'windstream.net', 'centurylink.net',
  'q.com', 'juno.com', 'netzero.net', 'mindspring.com', 'pacbell.net',
  'ameritech.net', 'swbell.net', 'prodigy.net', 'cableone.net', 'mediacombb.net',
  'suddenlink.net', 'wowway.com', 'rcn.com', 'hawaii.rr.com',
])

/** Hosts lent to many unrelated customers: tenant, mailing-list and site-builder domains. */
export const SHARED_TENANT_DOMAINS: ReadonlySet<string> = new Set<string>([
  // Microsoft 365 default tenant domains and tenant-hosted SharePoint, the
  // US government clouds (GCC, GCC High: ".us") included
  'onmicrosoft.com', 'onmicrosoft.de', 'onmicrosoft.us', 'onmschina.cn',
  'sharepoint.com', 'sharepoint.cn', 'sharepoint.de', 'sharepoint.us',
  // Mailing lists and bulk-mail relays
  'googlegroups.com', 'groups.io', 'amazonses.com', 'sendgrid.net',
  'mailgun.org', 'mcsv.net', 'mcdlv.net', 'rsgsv.net', 'mailchimpapp.net',
  'hubspotemail.net', 'ccsend.com',
  // Website builders and app hosts
  'wixsite.com', 'squarespace.com', 'godaddysites.com', 'wordpress.com',
  'weebly.com', 'blogspot.com', 'github.io', 'netlify.app', 'vercel.app',
  'azurewebsites.net', 'herokuapp.com', 'myshopify.com',
])

const TWO_PART_TLDS = new Set(['co.uk', 'com.au', 'co.nz', 'co.jp', 'com.br', 'co.za', 'com.mx', 'co.in'])

/** 'Dana@Sub.Example.COM' -> 'example.com'; null for junk. The server's `registrable_domain`. */
export function registrableDomain(addressOrDomain?: string | null): string | null {
  let raw = (addressOrDomain ?? '').trim().toLowerCase()
  if (!raw) return null
  const at = raw.lastIndexOf('@')
  if (at >= 0) raw = raw.slice(at + 1)
  raw = raw.trim().replace(/^\.+|\.+$/g, '')
  if (!raw || !raw.includes('.') || raw.includes(' ')) return null
  const parts = raw.split('.')
  if (parts.length >= 3 && TWO_PART_TLDS.has(parts.slice(-2).join('.'))) return parts.slice(-3).join('.')
  return parts.slice(-2).join('.')
}

/** True when a domain could plausibly identify a firm. The server's `is_corporate_domain`. */
export function isCorporateDomain(domain?: string | null): boolean {
  const registrable = registrableDomain(domain)
  return Boolean(registrable) && !FREEMAIL_DOMAINS.has(registrable as string) && !SHARED_TENANT_DOMAINS.has(registrable as string)
}

/** Words around a name that no mailbox is built from: honorifics, suffixes, designations. */
const NAME_NOISE = new Set([
  'dr', 'mr', 'mrs', 'ms', 'mx', 'prof', 'sir',
  'jr', 'sr', 'ii', 'iii', 'iv', 'esq', 'phd', 'cpa', 'cfa', 'mba', 'md', 'pe',
  'ccim', 'sior', 'cre', 'cmb',
])

function asciiWords(text: string): string[] {
  const folded = text.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
  return folded.match(/[a-z]+/g) ?? []
}

/**
 * Is this mailbox plausibly the named person's own? The server's
 * `address_names_person`: the mailbox (before any "+tag") begins with, or has as
 * one of its parts, the first name or surname, or the first initial joined to the
 * surname. Names under three letters count only as a whole part; initials alone
 * never count. capitalmarkets@, deals@ and info@ are nobody's.
 */
export function addressNamesPerson(address?: string | null, name?: string | null): boolean {
  const raw = (address ?? '').trim().toLowerCase()
  if (!raw.includes('@')) return false
  const local = raw.split('@', 1)[0].split('+', 1)[0]
  const parts = local.match(/[a-z]+/g) ?? []
  const words = asciiWords(name ?? '').filter((word) => !NAME_NOISE.has(word))
  if (!parts.length || !words.length) return false
  const first = words[0]
  const surnames = new Set([words[words.length - 1]])
  if (words.length > 1) surnames.add(words.slice(1).join(''))
  const forms = new Set([first, ...surnames])
  if (words.length > 1) {
    for (const surname of surnames) {
      forms.add(first[0] + surname)
      forms.add(surname + first[0])
      forms.add(first + surname)
    }
  }
  if (parts.some((part) => part.length >= 2 && forms.has(part))) return true
  const compact = parts.join('')
  const prefixes = [first, ...surnames].filter((form) => form.length >= 3)
  if (words.length > 1) for (const surname of surnames) prefixes.push(first[0] + surname)
  return prefixes.some((prefix) => compact.startsWith(prefix))
}
